import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Tag, Typography, Button, Drawer, Form, InputNumber, Table,
    Select, message, Modal, Spin, Row, Col, Divider, Result, Space, Steps, Tabs, DatePicker, Input
} from 'antd';
import {
    PlusOutlined, CloudServerOutlined, EnvironmentOutlined,
    CheckCircleOutlined, RedoOutlined, ExclamationCircleOutlined,
    LoadingOutlined, FileTextOutlined, DownloadOutlined, GlobalOutlined, SaveOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════

// 云主机实例清单（复用腾讯云 CVM 管理维度：机型/规格/数量）
const TIER_1 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '2C4G', disk: 200, count: 1 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '2C4G', disk: 200, count: 1 },
];
const TIER_2 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '4C8G', disk: 500, count: 2 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '4C8G', disk: 500, count: 1 },
];
const TIER_3 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '8C16G', disk: 1000, count: 4 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '8C16G', disk: 1000, count: 2 },
];
const TIER_4 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '16C32G', disk: 2000, count: 8 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '16C32G', disk: 2000, count: 3 },
];

// 多种状态 Mock，方便演示完整交互 TODO: 联调时移除
const MOCK_STATES = {
    pending: {
        deployNo: 'DEP-test-P1', region: '广州', regionId: 'ap-guangzhou', deviceCount: 5000,
        config: { instances: TIER_2 },
        platformCost: 3200.00, enterpriseCost: null,
        auditStatus: 'pending', deployStatus: null,
        submitTime: '2026-06-23 09:00:00', deployTime: null, completeTime: null,
        failReason: null, rejectReason: null, rejectTime: null, bills: [],
    },
    approved_deploying: {
        deployNo: 'DEP-test-A1', region: '上海', regionId: 'ap-shanghai', deviceCount: 20000,
        config: { instances: TIER_3 },
        platformCost: 6800.00, enterpriseCost: 7200.00,
        auditStatus: 'approved', deployStatus: 'deploying',
        submitTime: '2026-06-22 14:00:00', deployTime: '2026-06-23 10:00:00', completeTime: null,
        failReason: null, rejectReason: null, rejectTime: null,
    },
    approved_completed: {
        deployNo: 'DEP-test-C1', region: '北京', regionId: 'ap-beijing', deviceCount: 80000,
        config: { instances: TIER_4 },
        platformCost: 15000.00, enterpriseCost: 16000.00,
        auditStatus: 'approved', deployStatus: 'completed',
        submitTime: '2026-03-15 09:00:00', deployTime: '2026-03-16 09:00:00', completeTime: '2026-03-16 11:30:00',
        failReason: null, rejectReason: null, rejectTime: null,
    },
    approved_failed: {
        deployNo: 'DEP-test-F1', region: '南京', regionId: 'ap-nanjing', deviceCount: 3000,
        config: { instances: TIER_2 },
        platformCost: 3200.00, enterpriseCost: 3500.00,
        auditStatus: 'approved', deployStatus: 'failed',
        submitTime: '2026-06-20 10:00:00', deployTime: '2026-06-20 11:00:00', completeTime: null,
        failReason: '服务器开通超时，请检查网络配置后重试', rejectReason: null, rejectTime: null,
    },
    rejected: {
        deployNo: 'DEP-test-R1', region: '成都', regionId: 'ap-chengdu', deviceCount: 1000,
        config: { instances: TIER_1 },
        platformCost: 1200.00, enterpriseCost: 1500.00,
        auditStatus: 'rejected', deployStatus: null,
        submitTime: '2026-06-21 08:00:00', deployTime: null, completeTime: null,
        failReason: null, rejectReason: '申请资料不完整，请补充企业资质证明后重新提交', rejectTime: '2026-06-21 14:30:00',
    },
    approved_upgrading: {
        deployNo: 'DEP-test-U1', region: '北京', regionId: 'ap-beijing', deviceCount: 5000,
        config: { instances: TIER_4 },
        platformCost: 15000.00, enterpriseCost: 16000.00,
        auditStatus: 'approved', deployStatus: 'upgrading',
        submitTime: '2026-03-15 09:00:00', deployTime: '2026-03-16 09:00:00', completeTime: '2026-03-16 11:30:00',
        failReason: null, rejectReason: null, rejectTime: null,
    },
};
const MOCK_RECORD_DEFAULT = 'approved_completed';

const MOCK_REGIONS = [
    { label: '广州', value: 'ap-guangzhou' },
    { label: '上海', value: 'ap-shanghai' },
    { label: '北京', value: 'ap-beijing' },
    { label: '南京', value: 'ap-nanjing' },
    { label: '成都', value: 'ap-chengdu' },
    { label: '重庆', value: 'ap-chongqing' },
    { label: '香港', value: 'ap-hongkong' },
    { label: '新加坡', value: 'ap-singapore' },
    { label: '东京', value: 'ap-tokyo' },
    { label: '硅谷', value: 'na-siliconvalley' },
];

const MOCK_SERVER_CONFIG = [
    { region: 'ap-guangzhou', minDevices: 1, maxDevices: 3000, instances: TIER_1, monthlyFee: 1200 },
    { region: 'ap-guangzhou', minDevices: 3001, maxDevices: 10000, instances: TIER_2, monthlyFee: 3200 },
    { region: 'ap-guangzhou', minDevices: 10001, maxDevices: 50000, instances: TIER_3, monthlyFee: 6500 },
    { region: 'ap-guangzhou', minDevices: 50001, maxDevices: 100000, instances: TIER_4, monthlyFee: 15000 },
    { region: 'ap-shanghai', minDevices: 1, maxDevices: 3000, instances: TIER_1, monthlyFee: 1300 },
    { region: 'ap-shanghai', minDevices: 3001, maxDevices: 10000, instances: TIER_2, monthlyFee: 3400 },
    { region: 'ap-shanghai', minDevices: 10001, maxDevices: 50000, instances: TIER_3, monthlyFee: 6800 },
    { region: 'ap-shanghai', minDevices: 50001, maxDevices: 100000, instances: TIER_4, monthlyFee: 16000 },
    { region: 'ap-beijing', minDevices: 1, maxDevices: 3000, instances: TIER_1, monthlyFee: 1200 },
    { region: 'ap-beijing', minDevices: 3001, maxDevices: 10000, instances: TIER_2, monthlyFee: 3200 },
    { region: 'ap-beijing', minDevices: 10001, maxDevices: 50000, instances: TIER_3, monthlyFee: 6500 },
    { region: 'ap-beijing', minDevices: 50001, maxDevices: 100000, instances: TIER_4, monthlyFee: 15000 },
];

// 模拟真实出账账单：首月非整月 → 升配拆分 → 按实际天数计费
// 部署日 2026-03-16，至 2026-07-20 的完整账单（当前月账单下月1日出）
const generateBills = (record) => {
    if (!record?.deployTime || !record?.enterpriseCost) return [];
    // 预计算 mock 账单（按实际天数 × 月单价 / 当月天数）
    const bills = [
        { key: 'bill-01', billNo: 'MON202603', month: '2026-03', billingDate: '2026-04-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '16/31 天',
            enterpriseUnit: 3500.00, enterprisePayable: 1806.45, platformUnit: 3200.00, platformPayable: 1651.61,
            note: '部署于3月16日，首月按16天计费' },
        { key: 'bill-02', billNo: 'MON202604', month: '2026-04', billingDate: '2026-05-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '30/30 天',
            enterpriseUnit: 3500.00, enterprisePayable: 3500.00, platformUnit: 3200.00, platformPayable: 3200.00 },
        { key: 'bill-03', billNo: 'MON202605A', month: '2026-05(上)', billingDate: '2026-06-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '10/31 天',
            enterpriseUnit: 3500.00, enterprisePayable: 1129.03, platformUnit: 3200.00, platformPayable: 1032.26,
            note: '5月11日升配至2w设备，此前10天按原配置' },
        { key: 'bill-04', billNo: 'MON202605B', month: '2026-05(下)', billingDate: '2026-06-01', deviceCount: 20000,
            configLabel: '8C16G / 1T / RDS:8C16G1T×2', serviceDuration: '21/31 天',
            enterpriseUnit: 7200.00, enterprisePayable: 4877.42, platformUnit: 6800.00, platformPayable: 4606.45,
            note: '升配后21天按新配置计费' },
        { key: 'bill-05', billNo: 'MON202606A', month: '2026-06(上)', billingDate: '2026-07-01', deviceCount: 20000,
            configLabel: '8C16G / 1T / RDS:8C16G1T×2', serviceDuration: '20/30 天',
            enterpriseUnit: 7200.00, enterprisePayable: 4800.00, platformUnit: 6800.00, platformPayable: 4533.33,
            note: '6月21日二次升配至8w设备' },
        { key: 'bill-06', billNo: 'MON202606B', month: '2026-06(下)', billingDate: '2026-07-01', deviceCount: 80000,
            configLabel: '16C32G / 2T / RDS:16C32G2T×3', serviceDuration: '10/30 天',
            enterpriseUnit: 16000.00, enterprisePayable: 5333.33, platformUnit: 15000.00, platformPayable: 5000.00 },
        { key: 'bill-07', billNo: 'MON202607', month: '2026-07', billingDate: '2026-08-01', deviceCount: 80000,
            configLabel: '16C32G / 2T / RDS:16C32G2T×3', serviceDuration: '20/31 天',
            enterpriseUnit: 16000.00, enterprisePayable: 10322.58, platformUnit: 15000.00, platformPayable: 9677.42,
            note: '当前计费周期，账单于8月1日出账' },
    ];
    return bills.reverse();
};

const AUDIT_STATUS = {
    pending: { text: '待审核', color: 'blue' },
    approved: { text: '已通过', color: 'green' },
    rejected: { text: '已驳回', color: 'red' },
};
const DEPLOY_STATUS = {
    deploying: { text: '部署中', color: 'processing' },
    upgrading: { text: '升级中', color: 'processing' },
    completed: { text: '已完成', color: 'green' },
    failed: { text: '部署失败', color: 'red' },
};
// 自定义域名 DNS 解析状态
const DNS_STATUS = {
    checking: { text: '解析中', color: 'processing' },
    resolved: { text: '已生效', color: 'green' },
    failed: { text: '解析失败', color: 'red' },
};

const matchServerConfig = (region, deviceCount) => {
    const candidates = MOCK_SERVER_CONFIG.filter(c => c.region === region);
    return candidates.find(c => deviceCount >= c.minDevices && deviceCount <= c.maxDevices) || null;
};

// 设备数格式化：万用 w
// 企业端弹性费用 mock（运维推送 → 直接展示；单价 = 成本单价 × 弹性云资源用量成本比）
const MOCK_ELASTIC_BILLS = [
    { key: 'eb-01', billNo: 'CLOUD20260715001', deployNo: 'DEP-test-C1', billingDate: '2026-07-15', itemName: 'COS存储', unitPrice: 0.72, usage: 123.4567 },
    { key: 'eb-02', billNo: 'CLOUD20260716001', deployNo: 'DEP-test-C1', billingDate: '2026-07-16', itemName: 'COS存储', unitPrice: 0.72, usage: 98.2341 },
    { key: 'eb-03', billNo: 'CLOUD20260717001', deployNo: 'DEP-test-C1', billingDate: '2026-07-17', itemName: 'CDN流量', unitPrice: 0.48, usage: 210.7890 },
    { key: 'eb-04', billNo: 'CLOUD20260718001', deployNo: 'DEP-test-C1', billingDate: '2026-07-18', itemName: 'COS存储', unitPrice: 0.72, usage: 156.7890 },
    { key: 'eb-05', billNo: 'CLOUD20260719001', deployNo: 'DEP-test-C1', billingDate: '2026-07-19', itemName: '动态带宽', unitPrice: 1.20, usage: 45.1234 },
    { key: 'eb-06', billNo: 'CLOUD20260720001', deployNo: 'DEP-test-C1', billingDate: '2026-07-20', itemName: 'COS存储', unitPrice: 0.72, usage: 88.6600 },
    { key: 'eb-07', billNo: 'CLOUD20260801001', deployNo: 'DEP-test-C1', billingDate: '2026-08-01', itemName: 'CDN流量', unitPrice: 0.48, usage: 302.1234 },
    { key: 'eb-08', billNo: 'CLOUD20260802001', deployNo: 'DEP-test-C1', billingDate: '2026-08-02', itemName: 'COS存储', unitPrice: 0.72, usage: 140.5000 },
];
const getElasticBills = (deployNo) => MOCK_ELASTIC_BILLS.filter(c => c.deployNo === deployNo);

const formatDeviceCount = (n) => {
    if (!n && n !== 0) return '--';
    if (n >= 10000 && n % 10000 === 0) return `${n / 10000}w`;
    if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
    return n?.toLocaleString();
};

// 设备数单位选项
const UNIT_OPTIONS = [
    { label: '万 (w)', value: 10000 },
];

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

const PrivateDeploy = () => {
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mockState, setMockState] = useState(MOCK_RECORD_DEFAULT);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [form] = Form.useForm();
    const [deviceUnit, setDeviceUnit] = useState(10000); // 默认万
    const [billDrawerOpen, setBillDrawerOpen] = useState(false);
    const [elasticRange, setElasticRange] = useState([]);
    const [customDomain, setCustomDomain] = useState(null);
    const [domainStatus, setDomainStatus] = useState(null);
    const [domainInput, setDomainInput] = useState('');
    const [domainEditing, setDomainEditing] = useState(false);

    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            const rec = mockState === 'empty' ? null : MOCK_STATES[mockState];
            setRecord(rec);
            setCustomDomain(null);
            setDomainStatus(null);
            setDomainInput('');
            setDomainEditing(false);
            setLoading(false);
        }, 300);
    }, [mockState]);

    const handleSubmit = (values) => {
        const regionObj = MOCK_REGIONS.find(r => r.value === values.region);
        const actualDevices = values.deviceCount * deviceUnit;
        const config = matchServerConfig(values.region, actualDevices);
        if (!config) { message.warning('暂无可匹配的服务器配置'); return; }
        Modal.confirm({
            title: '确认提交',
            icon: <ExclamationCircleOutlined />,
            content: '提交后将进入平台审核，审核通过后自动开通服务器并部署服务。',
            okText: '确认提交',
            onOk: () => {
                const newRecord = {
                    deployNo: record?.deployNo || `DEP-${Date.now().toString(36).toUpperCase()}`,
                    region: regionObj?.label || values.region,
                    regionId: values.region,
                    deviceCount: actualDevices,
                    config: { instances: config.instances },
                    platformCost: config.monthlyFee,
                    enterpriseCost: record?.enterpriseCost || null,
                    auditStatus: 'pending',
                    deployStatus: null,
                    submitTime: new Date().toLocaleString('zh-CN', { hour12: false }),
                    failReason: null,
                    rejectReason: null,
                    rejectTime: null,
                };
                setRecord(newRecord);
                setMockState('pending');
                setDrawerOpen(false);
                form.resetFields();
                message.success('私有部署申请已提交，请等待平台审核');
            },
        });
    };

    const handleOpenDrawer = () => setDrawerOpen(true);

    // 自定义域名：仅可配置一个，编辑保存后展示解析进度（Mock：联调时替换为接口提交）
    const startEditDomain = () => { setDomainInput((customDomain || '').replace(/^https:\/\//, '')); setDomainEditing(true); };
    const cancelEditDomain = () => { setDomainInput(''); setDomainEditing(false); };
    const removeDomain = () => { setCustomDomain(null); setDomainStatus(null); };

    const handleSaveDomain = () => {
        const val = (domainInput || '').trim();
        if (!val) { message.warning('请输入自定义域名'); return; }
        setCustomDomain(`https://${val}`);
        setDomainStatus('checking');
        setDomainEditing(false);
        setDomainInput('');
        // 模拟 DNS 解析检测：2 秒后置为已生效
        setTimeout(() => setDomainStatus('resolved'), 2000);
        message.success('自定义域名已保存');
    };

    // Mock 导出：联调时替换为接口导出
    const handleExport = () => {
        message.loading('正在导出数据...');
        setTimeout(() => message.success('导出成功'), 800);
    };

    if (loading) return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;

    // CNAME 目标地址：审核通过后由平台生成
    const cnameTarget = record?.auditStatus === 'approved' && record?.deployNo
        ? `${record.deployNo.toLowerCase()}.hemeng.cloud`
        : null;

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Title level={4} style={{ margin: 0 }}>私有部署</Title>
                    <Select value={mockState} onChange={setMockState} size="small" style={{ width: 180 }}
                        options={[
                            { label: '无记录（空状态）', value: 'empty' },
                            { label: '待审核', value: 'pending' },
                            { label: '已通过+部署中', value: 'approved_deploying' },
                            { label: '已通过+已完成', value: 'approved_completed' },
                            { label: '已通过+升级中', value: 'approved_upgrading' },
                            { label: '已通过+部署失败', value: 'approved_failed' },
                            { label: '已驳回', value: 'rejected' },
                        ]} />
                    <Text type="secondary" style={{ fontSize: 11 }}>演示切换</Text>
                </Space>
            </div>

            {!record ? (
                /* ── 空状态：从未申请 ── */
                <Result icon={<CloudServerOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                    title="尚未发起私有部署"
                    subTitle="点击下方按钮发起私有部署申请"
                    extra={<Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleOpenDrawer}>发起私有部署</Button>} />
            ) : (
                /* ── 已有记录 ── */
                <Row gutter={[16, 16]}>
                    {/* 状态卡片 */}
                    <Col span={24}>
                        <StatusCard record={record} onRedeploy={handleOpenDrawer} onViewBill={() => setBillDrawerOpen(true)} />
                    </Col>
                    {/* 服务器配置 */}
                    <Col span={12}>
                        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }}
                            title={<><EnvironmentOutlined style={{ marginRight: 8 }} />服务器配置</>}>
                            {record.config?.instances?.length ? (
                                <Table size="small" pagination={false} bordered dataSource={record.config.instances} rowKey={(i) => i.label}
                                    columns={[
                                        { title: '云主机', dataIndex: 'label' },
                                        { title: '数量', dataIndex: 'count', width: 70, render: (v) => `${v} 台` },
                                        { title: '机型', dataIndex: 'instanceType', width: 110 },
                                        { title: '规格', dataIndex: 'spec', width: 90 },
                                        { title: '磁盘', dataIndex: 'disk', width: 90, render: (v) => `${v}GB` },
                                    ]} />
                            ) : '--'}
                        </Card>
                    </Col>
                    {/* 自定义域名配置：有部署记录即展示 */}
                    <Col span={12}>
                        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }}
                            title={<><GlobalOutlined style={{ marginRight: 8 }} />自定义域名配置</>}
                            extra={<Tag color="blue" style={{ border: 'none', fontFamily: 'monospace' }}>解析方式：CNAME</Tag>}>
                            {/* CNAME 目标地址 */}
                            <div style={{ marginBottom: 12 }}>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>CNAME 目标地址</Text>
                                {cnameTarget ? (
                                    <Text copyable={{ text: cnameTarget }} style={{ fontFamily: 'monospace' }}>{cnameTarget}</Text>
                                ) : (
                                    <Text type="secondary">审核通过并部署后生成</Text>
                                )}
                            </div>
                            <Divider style={{ margin: '8px 0' }} />

                            {/* 域名状态展示：未配置 "-"，已配置 "域名 + 状态" */}
                            <div style={{ marginBottom: 12 }}>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>自定义域名</Text>
                                {customDomain ? (
                                    <Space size={8}>
                                        <Text style={{ fontFamily: 'monospace' }}>{customDomain}</Text>
                                        {domainStatus === 'checking' && <Spin size="small" />}
                                        {domainStatus ? (
                                            <Tag color={(DNS_STATUS[domainStatus] || {}).color} style={{ border: 'none', marginInlineEnd: 0 }}>
                                                {(DNS_STATUS[domainStatus] || {}).text || domainStatus}
                                            </Tag>
                                        ) : (
                                            <Text type="secondary">-</Text>
                                        )}
                                    </Space>
                                ) : (
                                    <Text type="secondary">-</Text>
                                )}
                            </div>

                            {/* 编辑 / 配置入口 */}
                            {domainEditing ? (
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input addonBefore="https://" placeholder="例如：iot.example.com" value={domainInput}
                                        onChange={(e) => setDomainInput(e.target.value)} />
                                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDomain}>保存</Button>
                                    <Button onClick={cancelEditDomain}>取消</Button>
                                </Space.Compact>
                            ) : customDomain ? (
                                <Space size={0}>
                                    <Button type="link" size="small" onClick={startEditDomain}>编辑</Button>
                                    <Button type="link" size="small" danger onClick={removeDomain}>删除</Button>
                                </Space>
                            ) : (
                                <Button type="dashed" block icon={<PlusOutlined />} onClick={startEditDomain}>配置自定义域名</Button>
                            )}

                            <Divider style={{ margin: '12px 0 8px' }} />
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.8 }}>
                                配置步骤：保存自定义域名后，到域名服务商添加 CNAME 记录，将域名指向上述目标地址，等待解析生效（通常 10 分钟）。
                            </Text>
                        </Card>
                    </Col>
                </Row>
            )}

            <DeployDrawer open={drawerOpen} onClose={() => { setDrawerOpen(false); form.resetFields(); setDeviceUnit(10000); }}
                form={form} onSubmit={handleSubmit} defaultRegion={record?.regionId} defaultDevices={record?.deviceCount}
                deviceUnit={deviceUnit} onUnitChange={setDeviceUnit} />

            {/* ── 账单 Drawer（企业端：月度费用 + 弹性费用）── */}
            <Drawer title="费用账单" open={billDrawerOpen} onClose={() => setBillDrawerOpen(false)} width={900}>
                {record && (
                    <Tabs defaultActiveKey="basic" items={[
                        {
                            key: 'basic',
                            label: '月度费用',
                            children: generateBills(record).length > 0 ? (
                                <Table
                                    title={() => (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
                                        </div>
                                    )}
                                    dataSource={generateBills(record)} size="small"
                                    scroll={{ x: 'max-content' }}
                                    pagination={{ pageSize: 12, showTotal: (t) => `共 ${t} 条账单` }}
                                    columns={[
                                        { title: '账单编号', dataIndex: 'billNo', width: 140, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
                                        { title: '结算月份', dataIndex: 'month', width: 100 },
                                        { title: '出账日', dataIndex: 'billingDate', width: 100 },
                                        { title: '设备数', dataIndex: 'deviceCount', width: 80, render: (v) => formatDeviceCount(v) },
                                        { title: '服务器配置', dataIndex: 'configLabel', width: 200, render: (v) => <Text style={{ fontSize: 11 }}>{v}</Text> },
                                        { title: '服务时长', dataIndex: 'serviceDuration', width: 90 },
                                        { title: '单月价格', dataIndex: 'enterpriseUnit', width: 110, render: (v) => <Text>¥ {v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                                        { title: '应付金额', dataIndex: 'enterprisePayable', width: 110, render: (v) => <Text strong>¥ {v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                                    ]}
                                    summary={() => {
                                        const bills = generateBills(record);
                                        const totalPayable = bills.reduce((s, b) => s + (b.enterprisePayable || 0), 0);
                                        return (
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell colSpan={7}><Text strong>基础配置累计</Text></Table.Summary.Cell>
                                                <Table.Summary.Cell>
                                                    <Text strong style={{ color: '#1890ff' }}>¥ {totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        );
                                    }} />
                            ) : <div style={{ textAlign: 'center', padding: 40, color: '#999' }}><Text type="secondary">暂无月度费用记录</Text></div>,
                        },
                        {
                            key: 'elastic',
                            label: '弹性费用',
                            children: (() => {
                                const bills = [...getElasticBills(record?.deployNo)].sort((a, b) => (a.billingDate < b.billingDate ? 1 : -1));
                                if (bills.length === 0) return (
                                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}><Text type="secondary">暂无弹性费用记录</Text></div>
                                );
                                const filtered = elasticRange.length === 2
                                    ? bills.filter(b => b.billingDate >= elasticRange[0].format('YYYY-MM-DD') && b.billingDate <= elasticRange[1].format('YYYY-MM-DD'))
                                    : bills;
                                const total = filtered.reduce((s, b) => s + (b.unitPrice * b.usage || 0), 0);
                                return (
                                    <>
                                        <Space style={{ marginBottom: 12 }}>
                                            <Text type="secondary">出账日区间：</Text>
                                            <RangePicker onChange={(dates) => setElasticRange(dates || [])} />
                                            <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
                                        </Space>
                                        <Table dataSource={filtered} size="small"
                                            pagination={{ pageSize: 12, showTotal: (t) => `共 ${t} 条` }}
                                            columns={[
                                                { title: '账单编号', dataIndex: 'billNo', width: 140, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
                                                { title: '出账日', dataIndex: 'billingDate', width: 110 },
                                                { title: '费用项', dataIndex: 'itemName', width: 140, render: (v) => <Tag color="purple" style={{ border: 'none' }}>{v}</Tag> },
                                                { title: '单价（元/G）', dataIndex: 'unitPrice', width: 120, render: (v) => <Text>¥ {v?.toFixed(2)}</Text> },
                                                { title: '用量（G）', dataIndex: 'usage', width: 130, render: (v) => <Text>{v?.toFixed(4)}</Text> },
                                                { title: '金额', key: 'payable', width: 120, render: (_, b) => <Text strong>¥ {(b.unitPrice * b.usage).toFixed(2)}</Text> },
                                            ]}
                                            summary={() => (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell colSpan={5}><Text strong>汇总金额（按查询条件）</Text></Table.Summary.Cell>
                                                    <Table.Summary.Cell>
                                                        <Text strong style={{ color: '#fa541c' }}>¥ {total.toFixed(2)}</Text>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            )} />
                                    </>
                                );
                            })(),
                        },
                    ]} />
                )}
            </Drawer>
        </div>
    );
};

// ══════════════════════════════════════════════
//  状态卡片
// ══════════════════════════════════════════════

const StatusCard = ({ record, onRedeploy, onViewBill }) => {
    const navigate = useNavigate();
    const auditTag = AUDIT_STATUS[record.auditStatus] || { text: record.auditStatus, color: 'default' };
    const deployTag = record.deployStatus ? (DEPLOY_STATUS[record.deployStatus] || { text: record.deployStatus, color: 'default' }) : null;
    // 升级中对企业透明，视为已完成
    const isCompleted = record.deployStatus === 'completed' || record.deployStatus === 'upgrading';
    const canRedeploy = record.auditStatus === 'rejected';
    const isDeployFailed = record.auditStatus === 'approved' && record.deployStatus === 'failed';
    const hasBills = record?.deployTime && record?.enterpriseCost;

    // Mock 部署阶段
    const deployPhases = [
        { title: '开通服务器', desc: '正在申请云服务器资源' },
        { title: '部署PaaS服务', desc: '正在部署平台核心服务' },
        { title: '部署SaaS服务', desc: '正在部署应用服务实例' },
        { title: '配置网络', desc: '正在配置网络与安全策略' },
    ];

    return (
        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Row justify="space-between" align="middle">
                <Col>
                    <Text strong style={{ fontSize: 15, marginRight: 12 }}>
                        部署编号：<Text style={{ fontFamily: 'monospace' }}>{record.deployNo}</Text>
                    </Text>
                    <Text type="secondary" style={{ marginRight: 16 }}>{record.region}</Text>
                    <Text type="secondary" style={{ marginRight: 16 }}>最大设备数 {formatDeviceCount(record.deviceCount)}</Text>
                    <Text type="secondary">提交时间 {record.submitTime}</Text>
                </Col>
                <Col>
                    <Space size={12}>
                        {!isCompleted && (
                            <Tag color={auditTag.color} style={{ border: 'none', fontSize: 14, padding: '4px 12px' }}>{auditTag.text}</Tag>
                        )}
                        {deployTag && (!isCompleted || record.deployStatus === 'completed') && <Tag color={deployTag.color} style={{ border: 'none', fontSize: 14, padding: '4px 12px' }}>{deployTag.text}</Tag>}
                        {isCompleted && (
                            <Button type="primary" icon={<CheckCircleOutlined />}
                                onClick={() => navigate('/app/self-saas')}>查看 SaaS 应用</Button>
                        )}
                        {hasBills && (
                            <Button icon={<FileTextOutlined />} onClick={onViewBill}>查看账单</Button>
                        )}
                        {canRedeploy && (
                            <Button type="primary" icon={<RedoOutlined />} onClick={onRedeploy}>重新申请</Button>
                        )}
                        {isDeployFailed && (
                            <Text type="warning">等待平台管理员重新部署</Text>
                        )}
                    </Space>
                </Col>
            </Row>

            <div style={{ marginTop: 12 }}>
                {record.auditStatus === 'pending' && <Text type="secondary">平台审核通过后将自动部署</Text>}
                {record.deployStatus === 'deploying' && (
                    <div style={{ marginTop: 12, padding: '16px 20px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                        <Row align="middle" style={{ marginBottom: 12 }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} style={{ marginRight: 8 }} />
                            <Text strong style={{ color: '#52c41a' }}>部署进行中</Text>
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>预计耗时 5-10 分钟</Text>
                        </Row>
                        <Steps size="small" direction="vertical" current={Math.floor(Math.random() * 3) + 1}
                            items={deployPhases.map(p => ({ title: p.title, description: p.desc }))} />
                    </div>
                )}
                {record.deployStatus === 'failed' && record.failReason && <Text type="danger">失败原因：{record.failReason}</Text>}
                {isCompleted && record.completeTime && <Text type="success">完成时间：{record.completeTime}</Text>}
            </div>

            {record.auditStatus === 'rejected' && record.rejectReason && (
                <div style={{ marginTop: 12, padding: '12px 16px', background: '#fff2f0', borderRadius: 8, borderLeft: '3px solid #ff4d4f' }}>
                    <Row><Text type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>驳回原因</Text></Row>
                    <Text>{record.rejectReason}</Text>
                    {record.rejectTime && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>驳回时间：{record.rejectTime}</Text>}
                </div>
            )}
        </Card>
    );
};

// ══════════════════════════════════════════════
//  发起 / 重新部署 Drawer
// ══════════════════════════════════════════════

const DeployDrawer = ({ open, onClose, form, onSubmit, defaultRegion, defaultDevices, deviceUnit, onUnitChange }) => {
    const [config, setConfig] = useState(null);
    const [regionsLoading, setRegionsLoading] = useState(false);

    const selectedRegion = Form.useWatch('region', form);
    const selectedDevices = Form.useWatch('deviceCount', form);

    const actualDevices = selectedDevices ? selectedDevices * deviceUnit : null;

    const loadRegions = useCallback(() => { setRegionsLoading(true); setTimeout(() => setRegionsLoading(false), 300); }, []);
    useEffect(() => { if (open) { loadRegions(); if (defaultRegion) form.setFieldsValue({ region: defaultRegion, deviceCount: defaultDevices ? defaultDevices / deviceUnit : undefined }); } }, [open, loadRegions, defaultRegion, defaultDevices, form, deviceUnit]);

    useEffect(() => {
        setConfig(selectedRegion && actualDevices ? matchServerConfig(selectedRegion, actualDevices) : null);
    }, [selectedRegion, actualDevices]);

    const canSubmit = selectedRegion && actualDevices && actualDevices >= 1 && actualDevices <= 100000 && config;

    return (
        <Drawer title="发起私有部署" open={open} onClose={onClose} width={640}
            footer={<Space style={{ float: 'right' }}><Button onClick={onClose}>取消</Button><Button type="primary" disabled={!canSubmit} onClick={() => form.submit()}>确认提交</Button></Space>}>
            <Form form={form} layout="vertical" onFinish={onSubmit} initialValues={{ region: defaultRegion }}>
                <Form.Item label="服务商"><InputNumber style={{ width: '100%' }} disabled value="腾讯云" /></Form.Item>
                <Form.Item name="region" label="服务区域" rules={[{ required: true, message: '请选择服务区域' }]}>
                    <Select placeholder="请选择服务区域" showSearch optionFilterProp="label" loading={regionsLoading} notFoundContent={regionsLoading ? <Spin size="small" /> : '暂无可用区域'} options={MOCK_REGIONS} />
                </Form.Item>
                <Form.Item name="deviceCount" label="最大设备数" rules={[{ required: true, message: '请输入最大设备数' }]}>
                    <Space.Compact style={{ width: '100%' }}>
                        <InputNumber style={{ flex: 1 }} placeholder="请输入数量" min={0} step={1}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(v) => v.replace(/,/g, '')} />
                        <Select value={deviceUnit} onChange={onUnitChange} style={{ width: 90 }}
                            options={UNIT_OPTIONS} />
                    </Space.Compact>
                    {actualDevices > 0 && <Text type="secondary" style={{ fontSize: 12 }}>实际设备数：{actualDevices.toLocaleString()}（{formatDeviceCount(actualDevices)}）</Text>}
                </Form.Item>
            </Form>
            <Divider />
            <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 16 }}>
                <Text strong style={{ fontSize: 14, marginBottom: 12, display: 'block' }}>匹配配置预览</Text>
                {config ? (
                    <Table size="small" pagination={false} bordered dataSource={config.instances} rowKey={(i) => i.label}
                        columns={[
                            { title: '云主机', dataIndex: 'label' },
                            { title: '数量', dataIndex: 'count', width: 70, render: (v) => `${v} 台` },
                            { title: '机型', dataIndex: 'instanceType', width: 110 },
                            { title: '规格', dataIndex: 'spec', width: 90 },
                            { title: '磁盘', dataIndex: 'disk', width: 90, render: (v) => `${v}GB` },
                        ]} />
                ) : <Text type={selectedDevices ? 'danger' : 'secondary'} style={{ fontSize: 13 }}>{selectedDevices ? '暂无可匹配的服务器配置，请联系管理员' : '请选择区域并输入最大设备数'}</Text>}
            </div>
        </Drawer>
    );
};

export default PrivateDeploy;
